import React from 'react';
import { Form, Modal, Row, Col } from 'react-bootstrap';
import Select from 'react-select';

// Services & Helpers
import InstructorService from 'services/InstructorService';
import LocationService from 'services/LocationService';
import SubAgencyService from 'services/SubAgencyService';
import GlobalStateService from 'services/GlobalStateService';
import UserService from 'services/UserService';
import BootboxHelper from 'helpers/BootboxHelper';
import TextHelpers from 'helpers/TextHelpers';

// Components
import Loader from 'components/common/Loader';

//-------------------------------------------------------------------------------------------------------------------

class EditUserModal extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            isOpen: false
        };
    }

    show(opts) {
        return new Promise((resolve, reject) => {
            this.opts = opts;
            this.userID = opts.userID;
            this.resolve = resolve;
            this.loginInfo = GlobalStateService.getValue('loginInfo');
            this.subAgencyID = opts.subAgencyID;
            this.role = opts.role;

            this.setState({
                isOpen: true,
                isLoading: true
            });
            this.load();
        });
    }

    close() {
        this.setState({
            isOpen: false
        });
    }

    async load() {
        let user;
        if (this.userID) {
            user = await UserService.get(this.userID);
        } else {
            user = {
                role: (this.role || 'Vendor'),
                sendContractEmails: true,
                sendBookingEmails: true,
                sendFinancialEmails: true,
                subAgencyID: (this.subAgencyID || null),
                instructorIDs: [],
                geoIDs: {}
            };

            // Tick default geo
            const loginInfo = GlobalStateService.getValue('loginInfo');
            if (loginInfo.site.defaultGeoID) {
                user.geoIDs[loginInfo.site.defaultGeoID] = true;
            }
        }
        const instructors = await InstructorService.list();
        const subAgencies = await SubAgencyService.list();
        const geos = await LocationService.listGeos();
        this.setState({
            user,
            instructors,
            subAgencies,
            geos,
            isLoading: false
        });
    }

    updateFields(values) {
        const user = { ...this.state.user };
        for (var field in values) {
            let value = values[field];
            user[field] = value;
        }
        this.setState({ user });
    }

    async saveAndClose() {
        this.setState({
            isLoading: true
        });
        try {
            await UserService.save(this.state.user);
            this.close();
            this.resolve();
        } catch (error) {
            BootboxHelper.alert(error);
            this.setState({
                isLoading: false
            });
        }
    }

    async confirmDelete() {
        const confirm = await BootboxHelper.confirm('Are you sure you want to delete this user?');
        if (confirm) {
            this.delete();
        }
    }

    async delete() {
        this.setState({
            isLoading: true
        });
        await UserService.delete(this.state.user.id);
        this.close();
        this.resolve();
    }

    toggleGeo(id) {
        const geoIDs = { ...this.state.user.geoIDs };
        if (geoIDs[id]) {
            delete geoIDs[id];
        } else {
            geoIDs[id] = true;
        }
        this.updateFields({ geoIDs });
    }

    render() {
        const {
            isOpen
        } = this.state;

        if (!isOpen) {
            return null;
        }

        return (<>

            <Modal show className="edit-user-modal">
                {this.renderInner()}
            </Modal>

        </>);
    }

    renderInner() {
        const {
            isLoading,
            user,
            instructors,
            subAgencies,
            geos
        } = this.state;

        if (isLoading) {
            return (
                <Modal.Body>
                    <Loader />
                </Modal.Body>
            );
        }
        const loginInfo = GlobalStateService.getValue('loginInfo');

        const roles = [
            { id: 'Accountant', name: 'Accountant' },
            { id: 'Admin', name: 'Admin' },
            { id: 'Vendor', name: 'Vendor' },
            { id: 'Instructor', name: 'Instructor', isInstructor: true },
            { id: 'Manager', name: 'Manager' },
            { id: 'InstructorManager', name: 'Instructor & Manager', isInstructor: true }
        ];
        const isReadOnly = ((user.isSiteAdmin || user.isSuperAdmin) && !loginInfo.isSuperAdmin);
        const isInstructor = (user.role == 'Instructor' || user.role == 'InstructorManager' || isReadOnly);

        return (

            <form onSubmit={e => { e.preventDefault(); this.saveAndClose() }} className={isReadOnly ? ' read-only-form' : ''}>
                <Modal.Body>

                    <Row>

                        <Col md="6">

                            <Form.Group className="mb-3">
                                <Form.Label>First Name</Form.Label>
                                <input
                                    className="form-control"
                                    type="text"
                                    value={user.firstName || ''}
                                    onChange={e => this.updateFields({ firstName: e.target.value })}
                                    readOnly={isInstructor}
                                />
                            </Form.Group>

                        </Col>

                        <Col md="6">

                            <Form.Group className="mb-3">
                                <Form.Label>Last Name</Form.Label>
                                <input
                                    className="form-control"
                                    type="text"
                                    value={user.lastName || ''}
                                    onChange={e => this.updateFields({ lastName: e.target.value })}
                                    readOnly={isInstructor}
                                />
                            </Form.Group>

                        </Col>

                    </Row>

                    <Row>

                        <Col md="6">

                            <Form.Group className="mb-3">
                                <Form.Label>Email</Form.Label>
                                <input
                                    className="form-control"
                                    type="email"
                                    value={user.email || ''}
                                    onChange={e => this.updateFields({ email: e.target.value })}
                                    readOnly={isInstructor} 
                                />
                            </Form.Group>

                        </Col>

                        <Col md="6">

                            <Form.Group className="mb-3">
                                <Form.Label>Role</Form.Label>
                                <select
                                    className="form-control"
                                    value={user.role || ''}
                                    onChange={e => this.updateFields({ role: e.target.value })}
                                    disabled={isReadOnly}
                                >
                                    {roles.filter(r => !isInstructor || r.isInstructor).map(r =>
                                        <option key={r.id} value={r.id}>
                                            {r.name}
                                        </option>
                                    )}
                                </select>
                            </Form.Group>

                        </Col>

                    </Row>
                    
                    {user.role == 'Admin' && loginInfo.isSuperAdmin && user.id != loginInfo.user.id &&
                        <Form.Group className="mb-3">
                            <Form.Check
                                type="checkbox"
                                id="is-site-admin"
                                checked={!!user.isSiteAdmin}
                                onChange={e => this.updateFields({ isSiteAdmin: e.target.checked })}
                                label="Site Admin (can view financial information)"
                            />
                        </Form.Group>
                    }

                    <Form.Group className="mb-3">
                        <Form.Label>Geos</Form.Label>
                        
                        {geos.map(geo =>
                            <Form.Check
                                key={geo.id}
                                type="checkbox"
                                label={geo.name}
                                id={`geo-${geo.id}`}
                                checked={user.geoIDs[geo.id] || false}
                                onChange={e => this.toggleGeo(geo.id)}
                                disabled={isInstructor}
                            />
                        )}
                    </Form.Group>

                    {user.role == 'Vendor' &&
                        <Form.Group className="mb-3">
                            <Form.Check
                                type="checkbox"
                                id="can-make-enquiries"
                                checked={!!user.canMakeEnquiries}
                                onChange={e => this.updateFields({ canMakeEnquiries: e.target.checked })}
                                label="Can make enquiries"
                            />
                        </Form.Group>
                    }

                    {(user.role == 'Manager' || user.role == 'InstructorManager') &&
                        <Form.Group className="mb-3">
                            <Form.Label>Manager of sub-agency</Form.Label>
                            <select
                                className="form-control"
                                value={user.subAgencyID || ''}
                                onChange={e => this.updateFields({ subAgencyID: e.target.value })}
                            >
                                <option value="">(None)</option>
                                {subAgencies.map(sa =>
                                    <option key={sa.id} value={sa.id}>
                                        {sa.name}
                                    </option>
                                )}
                            </select>
                        </Form.Group>
                    }

                    {!!user.subAgencyID && <>
                        
                        <Form.Group className="mb-3">
                            <Form.Label>Automated emails</Form.Label>
                            <Form.Check
                                type="checkbox"
                                id="send-booking-emails"
                                checked={!!user.sendBookingEmails}
                                onChange={e => this.updateFields({ sendBookingEmails: e.target.checked })}
                                label="Booking emails"
                            />
                            <Form.Check
                                type="checkbox"
                                id="send-contract-emails"
                                checked={!!user.sendContractEmails}
                                onChange={e => this.updateFields({ sendContractEmails: e.target.checked })}
                                label="Contract emails"
                            />
                            <Form.Check
                                type="checkbox"
                                id="send-financial-emails"
                                checked={!!user.sendFinancialEmails}
                                onChange={e => this.updateFields({ sendFinancialEmails: e.target.checked })}
                                label="Financial emails"
                            />
                        </Form.Group>

                    </>}

                    {(user.role == 'Instructor' || user.role == 'InstructorManager') &&
                        <Form.Group className="mb-3">
                            <Form.Label>Linked instructor</Form.Label>
                            <Select
                                className="react-select-container"
                                classNamePrefix="react-select"
                                options={instructors.filter(i => !user.subAgencyID || i.subAgencyID == user.subAgencyID)}
                                value={instructors.filter(i => i.id == user.instructorID)}
                                getOptionValue={i => i.id}
                                getOptionLabel={i => TextHelpers.formatInstructorName(i)}
                                onChange={value => {
                                    this.updateFields({
                                        instructorID: value.id
                                    })
                                }}
                            />
                        </Form.Group>
                    }

                    {!isReadOnly &&
                        <Form.Group>
                            <Form.Label>{user.id ? 'Change ' : 'Set '} Password...</Form.Label>
                            <input
                                className="form-control"
                                type="password"
                                onChange={e => this.updateFields({ newPassword: e.target.value })}
                            />
                        </Form.Group>
                    }

                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="btn btn-secondary me-auto" onClick={() => this.close()}>
                        Close
                    </button>
                    {!isReadOnly && user.id &&
                        <button type="button" className="btn btn-danger ms-auto me-auto" onClick={() => this.confirmDelete()}>
                            Delete
                        </button>
                    }
                    {!isReadOnly &&
                        <button type="submit" className="btn btn-primary ms-auto">
                            Save
                        </button>
                    }
                </Modal.Footer>
            </form>
        );
    }
}

export default EditUserModal;